import Swiper from "swiper";
import { Pagination, A11y, Navigation } from 'swiper/modules';
import { Movie } from "@components/movie/Movie";

const selectors = {
  paginationContainer: ".swiper-dots",
  swiperNextButton: ".swiper-next",
  swiperPrevButton: ".swiper-prev",
  teaserGroupItem: ".teaser-group-item",
  slide: ".swiper-slide",
  accordionDetails: ".comp-accordion details",
  teaserGroup: ".teaser-group",
  movieOverlay: ".comp-card-movie-overlay",
  tab: ".comp-tab",
};

const attributes = {
  colMd: "data-col-md",
  colLg: "data-col-lg",
  showCarouselOn: "data-show-carousel-on",
  teaserGroupId: "data-t-id",
}

const classes = {
  workflowMode: "workflow-mode",
  optimize: "teaser-group-optimize",
  edgeLeftIndicator: "edge-left-indicator",
  edgeRightIndicator: "edge-right-indicator",
  slide: "swiper-slide",
  wrapper: "swiper-wrapper",
  swiper: "swiper",
} 


class TeaserGroup {
  private el: HTMLElement;
  private columnsSmall: number;
  private columnsMedium: number;
  private columnsLarge: number;
  private carouselOnViewport: string;
  private teaserGroup: HTMLElement;
  private movieOverlays: HTMLElement[];
  private hasWorkflowModeOn: boolean;
  private initialized: boolean;
  private isSwiper: boolean;
  private swiper: Swiper;
  private pagination: HTMLElement;
  private swiperNextButton: HTMLElement;
  private swiperPrevButton: HTMLElement;


  constructor(el: HTMLElement) {
    this.el = el;

    this.columnsSmall = 1;
    this.columnsMedium = parseInt(this.el.getAttribute(attributes.colMd));
    this.columnsLarge = parseInt(this.el.getAttribute(attributes.colLg));
    this.carouselOnViewport = this.el.getAttribute(attributes.showCarouselOn);

    this.teaserGroup = this.el.querySelector(selectors.teaserGroup);

    this.movieOverlays = Array.from(this.el.querySelectorAll(selectors.movieOverlay));

    this.hasWorkflowModeOn = this.el.classList.contains(classes.workflowMode);

    this.pagination = this.el.querySelector(selectors.paginationContainer);

    this.swiperNextButton = this.el.querySelector(selectors.swiperNextButton);
    this.swiperPrevButton = this.el.querySelector(selectors.swiperPrevButton);
  }

  public init() {
    if (this.hasWorkflowModeOn) {
      this.ensureEveryWorkflowCardIsInitialized();
    }

    this.initialized = false;
    this.isSwiper = false;

    const accordionDetails = this.el.closest(selectors.accordionDetails);

    if (accordionDetails) {
      accordionDetails.addEventListener("toggle", () => {
        const isPanelOpen = accordionDetails.hasAttribute("open");

        if (isPanelOpen) {
          this.initSwiper();
        }
      });
    }

    if (this.el.closest(selectors.tab)) {
      document.addEventListener("tabsChanged", this.initSwiper);
    }

    document.addEventListener("viewportChange", this.initSwiper);
    this.initSwiper();

    this.movieOverlays.forEach((overlay) => {
      this.handleMovieOverlay(overlay);
    });
  }

  private addSwiperClasses = () => {
    const slides = this.el.querySelectorAll(selectors.teaserGroupItem);
    slides.forEach((slide) => {
      slide.classList.add(classes.slide);
    });

    this.teaserGroup.classList.add(classes.wrapper);
    this.el.classList.add(classes.swiper);
  }

  private removeSwiperClasses = () => {
    const slides = this.el.querySelectorAll(selectors.teaserGroupItem);
    slides.forEach((slide) => {
      slide.classList.remove(classes.slide);
    });

    this.teaserGroup.classList.remove(classes.wrapper);
    this.el.classList.remove(classes.swiper);
  }

  private initSwiper = () => {
    const viewport = document.querySelector("html").className;

    const isSmallVp =
      viewport.indexOf("small") >= 0 &&
      (this.carouselOnViewport == "small" ||
        this.carouselOnViewport == "medium" ||
        this.carouselOnViewport == "large");
    const isMediumVp =
      viewport.indexOf("medium") >= 0 &&
      (this.carouselOnViewport == "medium" ||
        this.carouselOnViewport == "large");
    const isLargeVp =
      viewport.indexOf("large") >= 0 && this.carouselOnViewport == "large";

    const shouldInitSwiper = isSmallVp || isMediumVp || isLargeVp;

    if (!shouldInitSwiper && this.isSwiper) {
      this.swiper?.destroy(true, true);
      this.removeSwiperClasses();
      this.isSwiper = false;
    } else if (shouldInitSwiper && !this.isSwiper) {
      this.addSwiperClasses();

      this.swiper = new Swiper(this.el, {
        init: false,
        loop: false,
        allowTouchMove: true,
        roundLengths: true,
        enabled: true,
        modules: [Pagination, A11y, Navigation],
        speed: 350,
        pagination: {
          el: this.pagination,
          type: "bullets",
          clickable: true,
          bulletClass: "swiper-bullet",
          bulletActiveClass: "swiper-bullet-active",
        },
        slidesPerView: this.columnsSmall,
        slidesPerGroup: this.columnsSmall,
        spaceBetween: 0,
        navigation: {
          nextEl: this.swiperNextButton,
          prevEl: this.swiperPrevButton,
          disabledClass: "swiper-disabled"
        },
        breakpoints: {
          // Tablet
          768: {
            enabled: true,
            slidesPerView: this.columnsMedium,
            slidesPerGroup: this.columnsMedium,
          },
          // Desktop and above
          1024: {
            enabled: true,
            slidesPerView: this.columnsLarge,
            slidesPerGroup: this.columnsLarge,
          }
        },
        on: {
          slideChange: (swiper) => {
            if (this.hasWorkflowModeOn) {
              this.updateEdgeIndicators(swiper);
            }
          },

          afterInit: () => {
            this.el.classList.remove(classes.optimize);
          }
        }
      });

      this.swiper?.init();
      this.isSwiper = true;
    }

    if (!this.initialized) {
      this.el.classList.remove(classes.optimize);
    }

    if (this.hasWorkflowModeOn) {
      this.setCssVariableWithHighestCardWorkflowHeight();
      if (this.isSwiper) {
        this.updateEdgeIndicators(this.swiper);
      }
    }

    this.initialized = true;
  }

  private handleMovieOverlay = (movieOverlay: HTMLElement) => {
    const movie = movieOverlay?.querySelector<HTMLElement>(".comp-movie");
    const movieCanvas = movie?.querySelector(".movie-canvas");

    movieCanvas?.setAttribute("data-auto-play", "false");

    movieOverlay?.addEventListener("click", () => {
      window.Lightbox?.openModal(movie);
      window.setTimeout(() => {
        if (movie) {
          Movie.initOnElement(movie);
        }
      }, 100);
    });

    movieOverlay?.addEventListener("keydown", (event) => {
      if (event.code === "Enter" || event.code === "Space") {
        event.preventDefault();
        movieOverlay.click();
      }
    });

    const card = movieOverlay.closest(".comp-card");
    const startMovieButton = card.querySelector(".start-video-button > button");

    startMovieButton?.addEventListener("click", () => movieOverlay.click());
  }

  private ensureEveryWorkflowCardIsInitialized = () => {
    const teaserGroupItems = this.el.querySelectorAll(selectors.teaserGroupItem);

    const template = document.createElement("template");
    template.innerHTML = `<div class="comp-card-workflow-container">
<span class="comp-card-workflow-mobile-separator"></span>
<span class="comp-card-step-input" data-empty="true"></span>
<p class="comp-card-step-label"></p></div>`.trim();

    teaserGroupItems.forEach((element) => {
      const workflowCardContainer = element.querySelector(
        ".comp-card-workflow-container",
      );
      if (!workflowCardContainer) {
        const card = element.querySelector(".card");
        if (card) {
          card.prepend(template.content.cloneNode(true));
        } else {
          console.warn(
            "No .card found in .teaser-group-item; cannot prepend workflow card container.",
          );
        }
      }
    });
  }

  private setCssVariableWithHighestCardWorkflowHeight = () => {
    let highestHeight = 0;
    this.el.querySelectorAll(".comp-card-workflow-container").forEach((element) => {
      const elementHeight = (element as HTMLElement).offsetHeight;
      if (elementHeight > highestHeight) {
        highestHeight = elementHeight;
      }
    });

    this.el.style.setProperty(
      "--teaser-group-workflow-highest-height",
      `${highestHeight}px`,
    );
  }

  private updateEdgeIndicators = (swiper: Swiper) => {
    const totalItems = swiper.slides.length;
    const slidesToShow = swiper.params.slidesPerView as number;
    const slides = this.el.querySelectorAll(selectors.slide);

    const activeIndex = swiper.activeIndex;
    const lastSlideIndex = activeIndex + slidesToShow - 1;

    const activeSlide = slides[activeIndex];
    const lastVisibleSlide = slides[lastSlideIndex];
    

    if (!(activeSlide || lastVisibleSlide) || totalItems <= slidesToShow) {
      return;
    }

    slides.forEach((slide) => slide.classList.remove(classes.edgeLeftIndicator, classes.edgeRightIndicator));

    // Check if there are more elements to the left
    if (activeIndex > 0) {
      activeSlide.classList.add(classes.edgeLeftIndicator);
    }

    // Check if there are more elements to the right
    if (lastSlideIndex < totalItems - 1) {
      lastVisibleSlide.classList.add(classes.edgeRightIndicator);
    }
  }
}

const components = {
  "teaser-group": TeaserGroup
};

export function initComponents(root = document) {
  Object.entries(components).forEach(([name, Component]) => {
    root.querySelectorAll<HTMLElement>(`[data-component="${name}"]`).forEach(el => {
      new Component(el).init();
    });
  });
}

if (document.readyState !== "loading") {
  initComponents();
} else {
  document.addEventListener("DOMContentLoaded", () => initComponents());
}